import axios from 'axios';

import { waitForTmaInitData } from '@common/services/tmaInitDataService';
import { tokenService } from '@common/services/tokenStore';
import { fetcher, APIError } from '@common/utils/fetcher';

export { tokenService };

export const AuthFailureReason = {
  INITIALIZING: 'initializing',
  LOADING_PROFILE: 'loading_profile',
  READY: 'ready',
  NOT_REGISTERED: 'not_registered',
  NETWORK_ERROR: 'network_error',
  ERROR: 'error',
} as const;

export type AuthState =
  (typeof AuthFailureReason)[keyof typeof AuthFailureReason];

export type SignInTmaResult =
  | { success: true; token: string }
  | { success: false; reason: typeof AuthFailureReason.NOT_REGISTERED }
  | {
      success: false;
      reason: typeof AuthFailureReason.NETWORK_ERROR;
      error: Error;
    };

export const signInTma = async (): Promise<SignInTmaResult> => {
  try {
    const initData = await waitForTmaInitData();

    if (!initData) {
      console.error('[signInTma] initData is empty after waiting!');
      return {
        success: false,
        reason: AuthFailureReason.NETWORK_ERROR,
        error: new Error('Telegram initData is not available'),
      };
    }

    const response = await fetcher<{ token: string }>({
      url: '/sign-in/tma',
      method: 'POST',
      data: { initData },
      auth: null,
    });

    if (response?.token) {
      tokenService.setToken(response.token);
      return { success: true, token: response.token };
    }

    return { success: false, reason: AuthFailureReason.NOT_REGISTERED };
  } catch (error) {
    if (error instanceof APIError) {
      return {
        success: false,
        reason: AuthFailureReason.NETWORK_ERROR,
        error,
      };
    }
    if (axios.isAxiosError(error)) {
      if ([401, 403, 404].includes(error.response?.status ?? 0)) {
        return {
          success: false,
          reason: AuthFailureReason.NOT_REGISTERED,
        };
      }
    }
    const normalizedError =
      error instanceof Error ? error : new Error(String(error));

    return {
      success: false,
      reason: AuthFailureReason.NETWORK_ERROR,
      error: normalizedError,
    };
  }
};
